
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Clock, Calendar, User, Bookmark, ToggleLeft, ToggleRight, Cpu, Eye, Link as LinkIcon } from 'lucide-react';
import { fetchArticleById } from '../services/dataService';
import { BlogPost } from '../types';

const ArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  
  // DUAL MODE STATE
  const [readMode, setReadMode] = useState<'HUMAN' | 'MACHINE'>('HUMAN');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      fetchArticleById(Number(id)).then(data => {
        if (data) {
            setArticle(data);
        } else {
            navigate('/articoli');
        }
        setLoading(false);
      });
    }
  }, [id, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-3 h-3 bg-black animate-ping"></div>
  </div>;

  if (!article) return null;

  const isMachine = readMode === 'MACHINE';

  // Helper to strip HTML for Machine Mode raw view
  const getRawText = (html: string) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || "";
  };

  return (
    <div className={`min-h-screen transition-colors duration-700 ${isMachine ? 'bg-black text-green-500 font-mono selection:bg-green-900 selection:text-white' : 'bg-white text-black font-sans'}`}>
        
        {/* MODE TOGGLE (FIXED) */}
        <div className="fixed top-24 right-6 z-50 flex items-center gap-3 mix-blend-difference text-white">
            <span className="text-[9px] font-bold uppercase tracking-widest opacity-60">{isMachine ? 'MACHINE_MODE' : 'HUMAN_MODE'}</span>
            <button onClick={() => setReadMode(isMachine ? 'HUMAN' : 'MACHINE')} className="hover:scale-110 transition-transform">
                {isMachine ? <ToggleRight size={32} className="text-green-500 fill-current" /> : <ToggleLeft size={32} className="text-white" />}
            </button>
        </div>

        <div className={`pt-32 px-6 md:px-12 pb-20 ${isMachine ? 'max-w-[1800px]' : 'max-w-5xl'} mx-auto transition-all duration-500`}>
            
            {/* Navigation */}
            <div className="mb-12 flex justify-between items-center border-b border-gray-800 pb-4">
                <Link to="/articoli" className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-colors group ${isMachine ? 'text-green-700 hover:text-green-400' : 'text-gray-400 hover:text-black'}`}>
                    <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform"/> {isMachine ? '../BACK_TO_ROOT' : "Torna all'archivio"}
                </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                
                {/* MAIN CONTENT */}
                <div className="lg:col-span-8">
                    <header className="mb-12 animate-slide-up">
                        <div className={`flex items-center gap-4 mb-6 text-xs font-bold uppercase tracking-widest ${isMachine ? 'text-green-800' : ''}`}>
                            <span className={`${isMachine ? 'border border-green-800 text-green-500 px-1' : 'bg-black text-white px-3 py-1'}`}>{article.category}</span>
                            <span className={`flex items-center gap-1 ${isMachine ? 'text-green-700' : 'text-gray-500'}`}><Clock size={12}/> {article.readTime} EXEC_TIME</span>
                        </div>
                        
                        <h1 className={`${isMachine ? 'text-4xl font-mono font-bold leading-tight mb-8 text-green-500' : 'text-5xl md:text-7xl font-serif font-medium leading-tight mb-8 text-black'}`} dangerouslySetInnerHTML={{ __html: article.title }}></h1>

                        <div className={`flex items-center justify-between border-y py-6 ${isMachine ? 'border-green-900 text-green-700 font-mono text-xs' : 'border-gray-200 text-gray-500'}`}>
                            <div className="flex items-center gap-4">
                                <div>
                                    <div className={`font-bold ${isMachine ? 'text-green-500' : 'text-black'}`}>{isMachine ? `AUTH: ${article.author}` : article.author}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar size={12}/> {article.date}
                            </div>
                        </div>
                    </header>

                    <article className={`prose prose-lg max-w-none leading-loose animate-slide-up delay-100 ${
                        isMachine ? 'font-mono text-green-400/90 prose-headings:text-green-500 prose-strong:text-green-400' : 'font-serif text-gray-800'
                    }`}>
                        <div className={`text-xl md:text-2xl mb-12 pl-6 italic rounded-r-lg ${
                            isMachine ? 'font-mono not-italic border-l-2 border-green-500 bg-green-900/10 p-4 text-green-300' : 'font-sans font-light text-gray-600 border-l-4 border-black'
                        }`}>
                            {isMachine ? `// SUMMARY_INIT: ${article.excerpt}` : article.excerpt}
                        </div>

                        {isMachine ? (
                             <div className="whitespace-pre-wrap opacity-90 text-sm">
                                 {getRawText(article.content)}
                             </div>
                        ) : (
                             /* RENDER WORDPRESS HTML CONTENT SAFELY */
                             <div dangerouslySetInnerHTML={{ __html: article.content }} />
                        )}
                    </article>
                </div>

                {/* SEMANTIC SIDEBAR */}
                <div className="lg:col-span-4">
                    <div className={`sticky top-32 ${isMachine ? 'border border-green-900 bg-green-900/5 p-4' : ''}`}>
                        <div className={`text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2 ${isMachine ? 'text-green-600' : 'text-gray-400'}`}>
                           {isMachine ? <Cpu size={14}/> : <Eye size={14}/>} 
                           {isMachine ? 'DETECTED_ENTITIES' : 'Concetti Rilevati'}
                        </div>

                        <div className="space-y-4">
                            {['Phishing', 'Social Engineering', 'Cognitive Bias'].map(term => (
                                <Link to={`/atlante?term=${term}`} key={term} className={`block p-4 border transition-all group ${
                                    isMachine ? 'border-green-900 hover:bg-green-900/20 text-green-500' : 'border-gray-200 hover:border-black bg-white'
                                }`}>
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="font-bold text-xs uppercase">{term}</span>
                                        <LinkIcon size={12} className="opacity-0 group-hover:opacity-100 transition-opacity"/>
                                    </div>
                                    <div className={`text-[10px] ${isMachine ? 'text-green-700' : 'text-gray-400'}`}>Database Entry available</div>
                                </Link>
                            ))}
                        </div>

                        <div className={`mt-12 pt-8 border-t ${isMachine ? 'border-green-900' : 'border-gray-200'}`}>
                            <div className={`text-[10px] uppercase mb-2 ${isMachine ? 'text-green-800' : 'text-gray-400'}`}>Citation String</div>
                            <code className={`block text-[10px] p-2 rounded ${isMachine ? 'bg-black border border-green-900 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                                {article.author}, "{article.title.replace(/<[^>]*>?/gm, '')}", Cyberumanità Journal, {article.date}.
                            </code>
                        </div>
                    </div>
                </div>

            </div>

        </div>
    </div>
  );
};

export default ArticleDetail;
